/**
 * Clean up all Supabase authentication-related data from browser storage
 *
 * This function removes all authentication tokens and session data stored by Supabase
 * from both localStorage and sessionStorage. It's useful for:
 * - Logging out users completely
 * - Clearing stale auth state after errors
 * - Switching between user accounts
 * - Handling authentication conflicts
 *
 * @remarks
 * The function targets keys that:
 * - Start with 'supabase.auth.' (standard Supabase auth keys)
 * - Contain 'sb-' (Supabase project-specific keys)
 *
 * @example
 * ```typescript
 * // Clean up after logout
 * await supabase.auth.signOut();
 * cleanupAuthState();
 *
 * // Clean up on auth error
 * try {
 *   await supabase.auth.getSession();
 * } catch (error) {
 *   cleanupAuthState();
 * }
 * ```
 *
 * @throws {never} Never throws - silently handles storage access errors
 *
 * @sideEffects
 * - Removes items from localStorage
 * - Removes items from sessionStorage
 * - Does NOT affect other storage keys
 */
export const cleanupAuthState = () => {
  try {
    // Remove standard auth tokens
    localStorage.removeItem('supabase.auth.token');

    // Remove all Supabase auth keys from localStorage
    Object.keys(localStorage).forEach((key) => {
      if (key.startsWith('supabase.auth.') || key.includes('sb-')) {
        localStorage.removeItem(key);
      }
    });

    // Remove from sessionStorage if in use
    Object.keys(sessionStorage).forEach((key) => {
      if (key.startsWith('supabase.auth.') || key.includes('sb-')) {
        sessionStorage.removeItem(key);
      }
    });
  } catch (e) {
    // no-op: storage might be unavailable in some environments
  }
};

/**
 * Key marking that a sign-out is in flight.
 *
 * @remarks
 * Lives in localStorage, not sessionStorage, so every open tab sees it:
 * Supabase broadcasts the sign-out to all of them, and a tab that reacted by
 * starting a fresh Keycloak sign-in would quietly restore the session we are
 * ending. Deliberately matches neither the 'supabase.auth.' nor the 'sb-'
 * pattern above, so {@link cleanupAuthState} leaves it in place.
 */
export const SIGNING_OUT_KEY = 'kc-signing-out';

/**
 * How long a sign-out suppresses the login page's automatic SSO redirect.
 * Covers the round-trip to Keycloak and back; after it lapses the login page
 * behaves normally again, so an abandoned sign-out cannot wedge the app.
 */
const SIGNING_OUT_TTL_MS = 30_000;

/** Record that a sign-out has started, in every tab. */
export const markSigningOut = () => {
  try {
    localStorage.setItem(SIGNING_OUT_KEY, String(Date.now()));
  } catch (e) {
    // no-op: storage might be unavailable in some environments
  }
};

/** Forget a pending sign-out. */
export const clearSigningOut = () => {
  try {
    localStorage.removeItem(SIGNING_OUT_KEY);
  } catch (e) {
    // no-op: storage might be unavailable in some environments
  }
};

/** True while a sign-out started within the last {@link SIGNING_OUT_TTL_MS}. */
export const isSigningOut = () => {
  try {
    const started = Number(localStorage.getItem(SIGNING_OUT_KEY));
    if (!started) return false;
    if (Date.now() - started > SIGNING_OUT_TTL_MS) {
      clearSigningOut();
      return false;
    }
    return true;
  } catch (e) {
    return false;
  }
};
